/**
 * Diff por líneas (LCS) para comparar un borrador de prompt con la versión publicada. Los prompts
 * son chicos (≤ 64 KB, cientos de líneas): O(n·m) alcanza sin librerías.
 */
export type DiffLine =
  | { kind: 'same'; text: string; a: number; b: number }
  | { kind: 'add'; text: string; b: number }
  | { kind: 'del'; text: string; a: number };

const MAX_CELLS = 4_000_000;

export function diffLines(before: string, after: string): DiffLine[] {
  const a = before.split('\n');
  const b = after.split('\n');
  const n = a.length;
  const m = b.length;
  if (n * m > MAX_CELLS) {
    // Demasiado grande para la tabla: todo lo viejo como borrado y lo nuevo como agregado.
    return [
      ...a.map((text, i): DiffLine => ({ kind: 'del', text, a: i + 1 })),
      ...b.map((text, j): DiffLine => ({ kind: 'add', text, b: j + 1 })),
    ];
  }
  // lcs[i][j] = largo de la subsecuencia común de a[i..] y b[j..].
  const lcs: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    const row = lcs[i];
    const next = lcs[i + 1];
    if (!row || !next) continue;
    for (let j = m - 1; j >= 0; j--) {
      row[j] = a[i] === b[j] ? (next[j + 1] ?? 0) + 1 : Math.max(next[j] ?? 0, row[j + 1] ?? 0);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i] ?? '', a: i + 1, b: j + 1 });
      i++;
      j++;
    } else if ((lcs[i + 1]?.[j] ?? 0) >= (lcs[i]?.[j + 1] ?? 0)) {
      out.push({ kind: 'del', text: a[i] ?? '', a: i + 1 });
      i++;
    } else {
      out.push({ kind: 'add', text: b[j] ?? '', b: j + 1 });
      j++;
    }
  }
  for (; i < n; i++) out.push({ kind: 'del', text: a[i] ?? '', a: i + 1 });
  for (; j < m; j++) out.push({ kind: 'add', text: b[j] ?? '', b: j + 1 });
  return out;
}

export function diffStats(lines: readonly DiffLine[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const l of lines) {
    if (l.kind === 'add') added++;
    else if (l.kind === 'del') removed++;
  }
  return { added, removed };
}
